/**
 * Общие примитивы экранов: каркас сворачиваемых секций, индикатор сохранения,
 * Автосохранение — общий примитив экранов (задача 23): таймер debounce и запись по
 * уходу фокуса. Используется полями без своей правки (числовое поле секции заявки).
 *
 * Модуль появился вместе с экраном «Администратор» (src/frontend/adminScreen.ts):
 * его секции оформлены так же, как секции заявки, поэтому общий код живёт здесь,
 * а не дублируется (AGENTS.md: никакого дублирования). Разметка собирается только
 * хелперами dom.ts (правило задачи 5).
 */
import { ApiError } from './api';
import { button, el, field, iconButton, type FieldControl, type MessageBox } from './dom';
import { LOGIN_HREF, navigate } from './router';
import { clearSession } from './session';

/** Задержка автосохранения: пока пользователь печатает, запросы не уходят. */
export const AUTOSAVE_DELAY_MS = 700;

/**
 * Что видит тренер вместо кнопки после публикации заявки: подтверждение участия
 * (сценарий 6). Текст живёт здесь, рядом с заметкой организатора (`TEAM_CONFIRMED_NOTE`),
 * потому что его дословно повторяют docs/spec.md и docs/architecture.md: правка текста —
 * правка документов, а не только кода.
 */
export const PUBLISHED_NOTE =
  'Ваша заявка опубликована, организаторы её видят. Вы можете возвращаться и делать ' +
  'изменения, вплоть до регистрационного собрания. Организаторы увидят Ваши изменения ' +
  'в реальном времени.';

/**
 * Заметка о подтверждении на экране «Заявка команды» (задача 43). Тот же факт, что у
 * тренера (`PUBLISHED_NOTE`), но сказанный организатору: в списках раздела всегда лежат
 * только подтверждённые заявки, поэтому блока подтверждения с чекбоксом на этом экране нет
 * — вместо него одна строка. Тексты стоят рядом: смысл подтверждения должен читаться
 * одинаково на обоих экранах.
 */
export const TEAM_CONFIRMED_NOTE = 'Заявка подтверждена, организаторы её видят.';

/** Текст ошибки для пользователя: сообщения сервера показываем как есть. */
export function errorText(err: unknown): string {
  return err instanceof ApiError ? err.message : 'Неизвестная ошибка. Попробуйте ещё раз.';
}

/** Ошибка загрузки экрана: истёкшая сессия — на экран входа, остальное — сообщением. */
export function showLoadError(err: unknown, message: MessageBox): void {
  if (err instanceof ApiError && err.status === 401) {
    clearSession();
    navigate(LOGIN_HREF);
    return;
  }
  message.show(errorText(err), 'error');
}

/**
 * Сворачиваемая секция экрана: заголовок, индикатор сохранения и содержимое.
 * `setTitle` позволяет менять заголовок на ходу — таблицы показывают в нём ещё и
 * количество строк, а оно меняется при добавлении и удалении.
 *
 * `options.subsection` строит не отдельную секцию, а блок внутри другой секции:
 * заголовок и индикатор идут строкой над содержимым, без <details> — так живут
 * подсекции «Приезд» и «Отъезд» внутри «Гостиницы и трансфера от организаторов»
 * (у каждой свой индикатор сохранения строк, у секции его нет).
 * `options.sectionClass` — необязательная метка секции для стилей.
 */
export function sectionFrame(
  title: string,
  body: HTMLElement,
  options: { sectionClass?: string; subsection?: boolean } = {},
): { root: HTMLElement; indicator: HTMLElement; setTitle: (text: string) => void } {
  const indicator = el('span', { className: 'save-indicator', attrs: { 'aria-live': 'polite' } });
  const titleNode = el('span', { className: 'section__title', text: title });
  const root = options.subsection
    ? el('div', {
        className: 'subsection',
        children: [
          el('div', { className: 'subsection__head', children: [titleNode, indicator] }),
          body,
        ],
      })
    : el('details', {
        className: options.sectionClass ? `section ${options.sectionClass}` : 'section',
        attrs: { open: true },
        children: [
          el('summary', {
            className: 'section__summary',
            children: [titleNode, indicator],
          }),
          body,
        ],
      });
  /** Меняет текст заголовка секции (заголовок — одна точка правды для счёта строк). */
  const setTitle = (text: string): void => {
    titleNode.textContent = text;
  };
  return { root, indicator, setTitle };
}

/** Индикатор сохранения секции: «Сохранение…», «Сохранено» или текст ошибки. */
export interface SaveStatus {
  pending(): void;
  saved(): void;
  error(err: unknown): void;
  /**
   * Сбрасывает показанную ошибку: её причина устранена (правка строки, к которой
   * относилась ошибка, закончилась). Состояния «Сохранение…» и «Сохранено» не
   * затираются — иначе крестик после успешного сохранения гасил бы результат.
   */
  clearError(): void;
}

/**
 * Привязывает индикатор к элементу .save-indicator секции.
 * `fail` — общий сбой экрана (истёкшая сессия, сбой загрузки): его обрабатывает экран,
 * остальные ошибки показываются рядом с секцией.
 */
export function saveStatus(fail: (err: unknown) => void, indicator: HTMLElement): SaveStatus {
  return {
    pending: () => {
      indicator.className = 'save-indicator';
      indicator.textContent = 'Сохранение…';
    },
    saved: () => {
      indicator.className = 'save-indicator save-indicator--saved';
      indicator.textContent = 'Сохранено';
    },
    error: (err) => {
      // Истёкшая сессия — общий сбой экрана (очистка сессии и возврат на вход),
      // остальные ошибки показываем рядом с секцией.
      if (err instanceof ApiError && err.status === 401) {
        fail(err);
        return;
      }
      indicator.className = 'save-indicator save-indicator--error';
      indicator.textContent = errorText(err);
    },
    clearError: () => {
      if (!indicator.classList.contains('save-indicator--error')) return;
      indicator.className = 'save-indicator';
      indicator.textContent = '';
    },
  };
}

/**
 * Отложенное автосохранение: запрос уходит после паузы в вводе или по уходу фокуса.
 * Так сохраняется только поле без своей правки — у полей с «галочкой» запроса до
 * неё нет вовсе.
 */
export interface Autosave {
  /** Планирует сохранение (вызывается на каждое изменение поля). */
  schedule(): void;
  /** Сохраняет немедленно, если есть отложенное изменение. */
  flush(): void;
}

/** Создаёт автосохранение с задержкой: без него каждое нажатие клавиши шло бы в API. */
export function autosave(save: () => void, delayMs = AUTOSAVE_DELAY_MS): Autosave {
  let timer: number | undefined;
  return {
    schedule(): void {
      window.clearTimeout(timer);
      timer = window.setTimeout(() => {
        timer = undefined;
        save();
      }, delayMs);
    },
    flush(): void {
      if (timer === undefined) return;
      window.clearTimeout(timer);
      timer = undefined;
      save();
    },
  };
}

/** Сохраняет при уходе фокуса за пределы контейнера (поле без своей правки). */
export function flushOnFocusOut(container: HTMLElement, flush: () => void): void {
  container.addEventListener('focusout', (event) => {
    const next = (event as FocusEvent).relatedTarget as Node | null;
    if (next && container.contains(next)) return;
    flush();
  });
}

/** Значение контрола строкой; контрола нет (вычисляемая колонка) — пустая строка. */
export function controlValue(control: FieldControl | null): string {
  // У чекбокса значение читается из `checked`: в `value` у него всегда «on».
  if (control instanceof HTMLInputElement && control.type === 'checkbox') {
    return String(control.checked);
  }
  return control?.value ?? '';
}

/** Ячейка таблицы: подпись колонки уходит в data-label (карточка на узком экране). */
export function cell(label: string, content: Node): HTMLTableCellElement {
  return el('td', { attrs: { 'data-label': label }, children: [content] });
}

/**
 * Ячейка без значения: подпись колонки есть, содержимого нет. Так выглядят вычисляемые
 * колонки строки в режиме редактирования (виды программы и документы спортсмена): их ведёт
 * диалог, поэтому в строке правки остаётся пустое место — прочерк-заглушка читался бы как
 * значение (правка организатора 20.10.2026).
 */
export function emptyCell(label: string): HTMLTableCellElement {
  return el('td', { attrs: { 'data-label': label } });
}

/** Шапка таблицы: подписи колонок. В карточной раскладке она скрыта (стили .table). */
export function tableHead(labels: string[]): HTMLTableSectionElement {
  return el('thead', {
    children: [
      el('tr', {
        children: labels.map((label) => el('th', { text: label, attrs: { scope: 'col' } })),
      }),
    ],
  });
}

/**
 * Значение ячейки таблицы: пустое поле показываем прочерком — как в просмотре секций
 * заявки (город, телефон и номер рейса могут быть не заполнены).
 */
export function valueCell(text: string | number): HTMLElement {
  const shown = String(text).trim();
  return el('span', { text: shown === '' ? '—' : shown });
}

/**
 * Список внутри ячейки таблицы: так на экране «Заявка команды» (задача 43) показаны виды
 * программы спортсмена, его документы и спортсмены выступления — по строке на элемент.
 * Пустой список — прочерк, как у пустого значения: пустая ячейка читалась бы как сбой показа.
 * В карточной раскладке строки списка переносятся, а не обрезаются (стили `.cell-list`).
 */
export function cellList(items: string[]): HTMLElement {
  if (items.length === 0) return valueCell('');
  return el('ul', {
    className: 'cell-list',
    children: items.map((item) => el('li', { text: item })),
  });
}

/** Сообщение пустой секции: данных нет — это состояние экрана, а не ошибка. */
export function emptyNote(text: string): HTMLElement {
  return el('p', { className: 'table__empty', text });
}

/** Строка таблицы только для чтения: у таблиц разделов нет ни правки, ни действий. */
function readonlyRow(cells: HTMLTableCellElement[]): HTMLTableRowElement {
  return el('tr', { children: cells });
}

/**
 * Таблица только для чтения: шапка, строки и — при необходимости — итоговая строка.
 * Так построены таблицы раздела «Организатор» и его экрана «Заявка команды» (задача 43):
 * раскладку дают общие стили `.table` (карточки на узком экране, таблица на широком).
 */
export function readonlyTable(options: {
  columns: string[];
  rows: HTMLTableCellElement[][];
  /** Итоговая строка таблицы (подвал): «Всего команд» у заявок, «Всего» у футболок. */
  footer?: HTMLTableSectionElement;
  /** Дополнительный класс таблицы (раскладка колонок задана в styles/components.css). */
  className?: string;
}): HTMLTableElement {
  return el('table', {
    className: options.className ? `table ${options.className}` : 'table',
    children: [
      tableHead(options.columns),
      el('tbody', { children: options.rows.map(readonlyRow) }),
      options.footer ?? null,
    ],
  });
}

/**
 * Список «подпись — значение» режима просмотра: так показаны секция «Организация» заявки,
 * секция «Турнир» экрана администратора и секция «Организация» экрана «Заявка команды»
 * (задача 43). Разметка (`.details`, `.details__term`, `.details__value`) одна, поэтому и
 * правило показа живёт в одном месте. Пустое значение показывается прочерком — как в
 * самой секции-форме.
 */
export function detailsList(items: { label: string; value: string }[]): HTMLElement {
  return el('dl', {
    className: 'details',
    children: items.flatMap((item) => [
      el('dt', { className: 'details__term', text: item.label }),
      el('dd', { className: 'details__value', text: item.value || '—' }),
    ]),
  });
}

/** Параметры чекбокса: отметка, доступное имя и обработчик переключения. */
export interface CheckboxOptions {
  checked: boolean;
  /** Доступное имя (`aria-label`): у флажка в таблице видимой подписи нет. */
  label: string;
  disabled?: boolean;
  onToggle?: (checked: boolean) => void;
}

/**
 * Чекбокс с доступным именем: общий примитив таблиц и панелей действий.
 * Отметка уходит обработчику сразу — общий код не решает, что с ней делать
 * (таблицы экрана администратора отправляют изменение на сервер, панель действий
 * хранит своё состояние).
 */
export function checkbox(options: CheckboxOptions): HTMLInputElement {
  const onToggle = options.onToggle;
  return el('input', {
    attrs: {
      type: 'checkbox',
      checked: options.checked,
      disabled: options.disabled ?? false,
      'aria-label': options.label,
    },
    on: onToggle
      ? {
          change: (event) => onToggle((event.target as HTMLInputElement).checked),
        }
      : undefined,
  });
}

/**
 * Диалог подтверждения на нативном <dialog> (стили .dialog): Esc — отказ.
 * Кнопки две: согласие помечено `confirmText` (опасное действие — красное) и отказ —
 * `cancelText` («Отмена» по умолчанию; экрану администратора нужны «Да»/«Нет»).
 * Узел диалога удаляется после закрытия, «висящих» элементов не остаётся.
 */
export function confirmDialog(
  question: string,
  confirmText: string,
  cancelText = 'Отмена',
): Promise<boolean> {
  return new Promise((resolve) => {
    let answer = false;
    const dialog = el('dialog', {
      className: 'dialog dialog--confirm',
      children: [
        el('p', { className: 'dialog__title', text: question }),
        el('div', {
          className: 'dialog__actions',
          children: [
            button({ text: cancelText, onClick: () => dialog.close() }),
            button({
              text: confirmText,
              variant: 'danger',
              onClick: () => {
                answer = true;
                dialog.close();
              },
            }),
          ],
        }),
      ],
    });
    dialog.addEventListener('close', () => {
      dialog.remove();
      resolve(answer);
    });
    document.body.append(dialog);
    dialog.showModal();
  });
}

/** Поле секции-формы: подпись, текст режима просмотра и контрол режима правки. */
export interface SectionField<T> {
  /** Идентификатор контрола: по нему <label for> связывает подпись с полем. */
  id: string;
  label: string;
  /** Значение в режиме просмотра; пустое показывается прочерком. */
  view(row: T): string;
  /** Контрол режима редактирования. */
  control(row: T): FieldControl;
}

/** Параметры секции-формы: состав полей, данные секции и её запись по «галочке». */
export interface FieldsSectionOptions<T> {
  title: string;
  /** Подпись кнопки «карандаш» (доступное имя для скринридера и подсказка). */
  editTitle: string;
  fields: SectionField<T>[];
  /** Данные секции из модели экрана (последний ответ сервера). */
  data(): T;
  /** Собирает значения секции из полей правки: позиция = позиция в `fields`. */
  fromValues(values: string[]): T;
  /** Открывать секцию сразу в правке: у пустой секции заполнять есть что. */
  startEditing?: boolean;
  /**
   * Запись секции по «галочке». Результат запроса применяет сам вызов (например,
   * экран заявки запоминает пришедшую заявку) — общий код о модели экрана не знает.
   */
  save(row: T): Promise<void>;
  /** Общий сбой экрана (истёкшая сессия, сбой загрузки). */
  fail(err: unknown): void;
}

/**
 * Секция-форма: поля «подпись — значение» с режимами просмотра и редактирования.
 * Пока идёт правка, в API не уходит ничего: «галочка» записывает значения,
 * «крестик» отменяет правку (изменения при этом теряются). Реализация одна на все
 * такие секции: они отличаются только составом полей и эндпоинтом сохранения,
 * поэтому режимы, индикатор и действия здесь общие и не дублируются. Так построены
 * секция «Организация» заявки и секция «Турнир» экрана администратора.
 */
export function fieldsSection<T>(options: FieldsSectionOptions<T>): HTMLElement {
  const body = el('div', { className: 'section__body' });
  const { root, indicator } = sectionFrame(options.title, body);
  const status = saveStatus(options.fail, indicator);

  /** Контролы текущей правки: позиция в массиве = позиция поля в `options.fields`. */
  let controls: FieldControl[] = [];

  /** Отправляет секцию на сервер по «галочке»; true — запрос прошёл. */
  async function save(): Promise<boolean> {
    status.pending();
    try {
      const values = controls.map((control) => controlValue(control));
      await options.save(options.fromValues(values));
      status.saved();
      return true;
    } catch (err) {
      status.error(err);
      return false;
    }
  }

  /** Режим просмотра секции-формы: статичный текст полей и «карандаш» в углу. */
  function renderView(): void {
    body.classList.remove('is-editing');
    const row = options.data();
    body.replaceChildren(
      el('div', {
        className: 'row-view',
        children: [
          detailsList(
            options.fields.map((item) => ({ label: item.label, value: item.view(row) })),
          ),
          el('div', {
            className: 'row-view__actions',
            children: [iconButton({ icon: 'edit', title: options.editTitle, onClick: renderEdit })],
          }),
        ],
      }),
    );
  }

  /** Режим редактирования секции-формы: поля ввода и «галочка»/«крестик». */
  function renderEdit(): void {
    // Правка начинается заново: ошибка прошлой попытки к ней не относится.
    status.clearError();
    body.classList.add('is-editing');
    const row = options.data();
    controls = [];
    const fields = options.fields.map((item) => {
      const control = item.control(row);
      controls.push(control);
      return field({ id: item.id, label: item.label, control });
    });

    body.replaceChildren(
      el('div', {
        className: 'row-edit',
        children: [
          el('div', {
            className: 'row-edit__fields',
            children: fields.map((item) => item.root),
          }),
          el('div', {
            className: 'row-edit__actions',
            children: [
              iconButton({ icon: 'check', title: 'Принять изменения', onClick: accept }),
              iconButton({ icon: 'close', title: 'Отменить изменения', onClick: cancel }),
            ],
          }),
        ],
      }),
    );
  }

  /** Галочка: записывает значения и возвращает секцию в режим просмотра. */
  function accept(): void {
    void save().then((saved) => {
      if (saved) renderView();
    });
  }

  /** Крестик: возвращает просмотр, не записывая правку. */
  function cancel(): void {
    // Правка окончена: ошибка сохранения относилась к ней, поэтому её снимаем.
    status.clearError();
    renderView();
  }

  if (options.startEditing) renderEdit();
  else renderView();
  return root;
}

/** Поле ввода текста. */
export function textControl(value: string): FieldControl {
  return inputControl('text', value);
}

/** Поле ввода с заданным типом: тип задаёт браузерную клавиатуру и проверку (email, tel). */
export function inputControl(type: string, value: string): FieldControl {
  return el('input', { attrs: { type, value } });
}

/**
 * Чекбокс: булево значение строки — колонка «Раннее заселение» в таблице «Приезд».
 * В просмотре значение показывается словами «Да»/«Нет», в правке — этим квадратом.
 */
export function checkboxControl(value: boolean): FieldControl {
  return el('input', { attrs: { type: 'checkbox', checked: value } });
}

/** Вариант дропдауна: значение, подпись и признак недоступности (уже выбран). */
export interface SelectOption {
  value: string;
  label: string;
  disabled?: boolean;
}

/**
 * Дропдаун со значениями справочника: выбранный вариант помечается атрибутом selected.
 * Общий для секций заявки (вокзал, виды программы, размер футболки) и периода журнала
 * аудита: разметка одна, поэтому «нативный список» выглядит одинаково на всех экранах.
 */
export function selectControl(options: SelectOption[], selected: string): HTMLSelectElement {
  return el('select', {
    children: options.map((item) =>
      el('option', {
        text: item.label,
        attrs: {
          value: item.value,
          selected: item.value === selected,
          disabled: item.disabled ?? null,
        },
      }),
    ),
  });
}
